import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AGENT_PAGES } from "../places";
import { useTheme } from "next-themes";
import { Sun, Moon, Menu } from "lucide-react";
import { useMediaQuery, useViewport } from "@/shared/hooks/useViewport";
import {
  DatabaseIcon, ListTodoIcon, SlidersHorizontalIcon, WaypointsIcon, RadioIcon,
  AgentIcon, ShieldCheckIcon, ScrollTextIcon,
} from "@/shared/components/icons/animated";
import Logo from "@/shared/components/Logo";
import { Tag } from "@/shared/components/teams-ui";

import type { AnimatedIconHandle, AnimatedIconProps } from "@/shared/components/icons/animated";
import type { View } from "@/shared/types";

/** A tab's glyph. The strip drives animated icons through a ref. */
type NavIcon = React.ForwardRefExoticComponent<
  AnimatedIconProps & React.RefAttributes<AnimatedIconHandle>
>;

/** One tab in the strip. `key` is the view it navigates to. */
interface NavItem {
  key: View;
  label: string;
  Icon: NavIcon;
  /** Marks the tab as an unfinished surface. */
  preview?: boolean;
}

/* Agent-scoped views — the only tabs; workspace pages are sidebar rows. */
const AGENT_NAV: NavItem[] = [
  { key: "chat", label: "Agent", Icon: AgentIcon },
  { key: "traces", label: "Traces", Icon: WaypointsIcon },
  // Evidence, then verdict — Evals sits next to Traces because it is the same
  // material read for a different purpose.
  { key: "evals", label: "Evals", Icon: ShieldCheckIcon },
  // What the agent told Builder it couldn't do: more evidence, fixed with Builder.
  { key: "reports", label: "Reports", Icon: ScrollTextIcon },
  { key: "tasks", label: "Tasks", Icon: ListTodoIcon },
  // the tables the agent keeps: beside Tasks, the other thing it does on its own
  { key: "spaces", label: "Spaces", Icon: DatabaseIcon },
  { key: "interfaces", label: "Interfaces", Icon: RadioIcon },
  { key: "settings", label: "Settings", Icon: SlidersHorizontalIcon },
];

/* Buttons living inside the utility chip — same quiet chrome as nav pills. */
export const invBtn =
  "press w-7 h-7 rounded-[8px] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]";

/* One tab: a quiet label on the tray's strip. The open one is marked by the white
   pill (`.tab-glide`) that slides under it, not by its own background. */
function Tab({ item, active, showLabel, onClick, dragging, onDragStart, onDragEnter, onDragEnd }: {
  item: NavItem;
  active: boolean;
  showLabel: boolean;
  onClick: () => void;
  dragging?: boolean;
  onDragStart?: React.DragEventHandler<HTMLButtonElement>;
  onDragEnter?: React.DragEventHandler<HTMLButtonElement>;
  onDragEnd?: React.DragEventHandler<HTMLButtonElement>;
}) {
  const ref = useRef<AnimatedIconHandle>(null);
  const { Icon } = item;
  return (
    <button
      onClick={onClick}
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      onMouseEnter={() => ref.current?.startAnimation()}
      onMouseLeave={() => ref.current?.stopAnimation()}
      title={item.preview ? `${item.label} — preview` : item.label}
      aria-current={active ? "page" : undefined}
      data-active={active || undefined}
      className={`press relative z-[1] shrink-0 h-8 rounded-[10px] flex items-center justify-center gap-2 text-[13px] whitespace-nowrap ${
        showLabel ? "px-3" : "px-2.5"
      } ${dragging ? "opacity-40" : ""} ${
        active ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      }`}
    >
      <Icon ref={ref} size={14} className="shrink-0" />
      {showLabel && <span>{item.label}</span>}
      {/* Preview marker — full tag when the label is showing, otherwise a dot in
          the tab's bottom-right so the squeezed strip still flags it (bottom,
          not top: several nav glyphs point up-right and would collide). */}
      {item.preview && (showLabel
        ? <Tag className="shrink-0">Preview</Tag>
        : <span className="absolute bottom-[5px] right-[5px] w-[5px] h-[5px] rounded-full bg-[var(--text-quaternary)]" />
      )}
    </button>
  );
}

export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  return (
    <button
      onClick={() => setTheme(dark ? "light" : "dark")}
      className={invBtn}
      title={dark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {dark ? <Sun className="w-[14px] h-[14px]" strokeWidth={1.75} /> : <Moon className="w-[14px] h-[14px]" strokeWidth={1.75} />}
    </button>
  );
}

/**
 * Destination switcher. Every destination is always a tab — nothing hides in a
 * menu. When the strip runs out of room the labels drop and the tabs squeeze to
 * icons; the active one keeps its label so "where am I" is always readable.
 */
function NavGroup({ items, active, onSelect, labels, drag }: {
  items: NavItem[];
  active: View;
  onSelect: (key: View) => void;
  labels: boolean;
  /** Present only where reordering is offered — desktop. */
  drag?: {
    dragKey: View | null;
    onDragStart: (key: View) => void;
    onDragEnter: (key: View) => void;
    onDragEnd: () => void;
  };
}) {
  return items.map((item) => {
    const isActive = item.key === active;
    return (
      <Tab
        key={item.key}
        item={item}
        active={isActive}
        showLabel={labels || isActive}
        onClick={() => onSelect(item.key)}
        dragging={drag?.dragKey === item.key}
        onDragStart={drag && ((e) => { e.dataTransfer.effectAllowed = "move"; drag.onDragStart(item.key); })}
        onDragEnter={drag && (() => drag.onDragEnter(item.key))}
        onDragEnd={drag?.onDragEnd}
      />
    );
  });
}

/* Where the white pill sits: under the open tab, measured after layout. It
   follows the tab when the order, the labels or the strip's width change. */
function useGlide(strip: React.RefObject<HTMLDivElement | null>, deps: unknown[]) {
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const el = strip.current;
    if (!el) return;
    const place = () => {
      const on = el.querySelector<HTMLElement>("[data-active]");
      setBox(on ? { left: on.offsetLeft, width: on.offsetWidth } : null);
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(el);
    return () => observer.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps -- the caller names what moves the open tab
  return box;
}

/* The tab strip on the top of the tray that holds the content card — agent views
   only (workspace pages and Builder open with no strip on desktop). Below lg it
   also carries the menu button that opens the sidebar sheet, and the logo. */
const NAV_ORDER_KEY = "agent-nav-order";

export default function TopBar({ view, definable = false, onNav, onAgentNav, onOpenMobileNav }: {
  view: View;
  /** Builder is open: nobody configures it, so it has no tabs — only the phone's menu button. */
  definable?: boolean;
  onNav: (view: View) => void;
  onAgentNav: (key: View) => void;
  onOpenMobileNav: () => void;
}) {
  const { isDesktop } = useViewport();
  // Under 1280 the strip can't carry every labelled tab, so labels drop and
  // the tabs squeeze to icons (the active one keeps its label).
  const isWide = useMediaQuery("(min-width: 1280px)");
  // User-arranged pill order. A tab that has gone (Onboarding moved into Settings) drops
  // out of a saved order and a new one joins at the end, so the rest keeps its place.
  const [navOrder, setNavOrder] = useState<View[]>(() => {
    const keys = AGENT_NAV.map((i) => i.key);
    try {
      // JSON.parse(null) parses the string "null" — the catch is what handles a
      // missing key, exactly as before.
      const saved: unknown = JSON.parse(localStorage.getItem(NAV_ORDER_KEY)!);
      if (Array.isArray(saved)) {
        const kept = keys.filter((k) => saved.includes(k)).sort((a, b) => saved.indexOf(a) - saved.indexOf(b));
        return [...kept, ...keys.filter((k) => !kept.includes(k))];
      }
    } catch {
      /* missing or corrupt localStorage → the default pill order below */
    }
    return keys;
  });
  const [dragKey, setDragKey] = useState<View | null>(null);

  // Live reorder while hovering — the pill slides out of the way before drop.
  const dragOver = (overKey: View) => {
    if (!dragKey || dragKey === overKey) return;
    setNavOrder((o) => {
      const next = o.filter((k) => k !== dragKey);
      next.splice(next.indexOf(overKey) + (o.indexOf(dragKey) < o.indexOf(overKey) ? 1 : 0), 0, dragKey);
      return next;
    });
  };
  const dragEnd = () => {
    setDragKey(null);
    setNavOrder((o) => {
      try {
        localStorage.setItem(NAV_ORDER_KEY, JSON.stringify(o));
      } catch {
        // no storage: the order holds until a reload
      }
      return o;
    });
  };

  const isAgentView = AGENT_PAGES.includes(view); // the tabs above, in their order
  // On a phone the active tab may sit past the edge of the scrolling strip — bring it into view.
  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isDesktop) tabsRef.current?.querySelector("[data-active]")?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [view, isDesktop]);
  // navOrder is always a permutation of AGENT_NAV's keys, so every lookup hits.
  const navItems = definable ? [] : navOrder.map((k) => AGENT_NAV.find((i) => i.key === k)!);

  const glide = useGlide(tabsRef, [view, navItems.map((i) => i.key).join(), isWide, isDesktop]);

  return (
    <header className="h-10 shrink-0 flex items-center min-w-0">
      <nav className="flex-1 min-w-0 h-full flex items-center gap-1 px-0.5">
        {!isDesktop && (
          <>
            <button
              onClick={onOpenMobileNav}
              title="Menu"
              className="press w-8 h-8 shrink-0 rounded-[10px] flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
            >
              <Menu className="w-[18px] h-[18px]" strokeWidth={1.75} />
            </button>
            <button onClick={() => onNav("overview")} title="Overview" className="press w-8 h-8 shrink-0 flex items-center justify-center">
              <Logo size={20} className="text-[var(--text-primary)]" />
            </button>
          </>
        )}
        {/* Below lg the tabs outgrow a phone, so they scroll sideways past the fixed
            menu and logo; scroll-px-1 stops a scrolled-to tab at the tray's edge. */}
        {isAgentView && (
          <div ref={tabsRef} className="relative min-w-0 h-full flex items-center gap-0.5 overflow-x-auto no-scrollbar scroll-px-1">
            {glide && <span aria-hidden="true" className="tab-glide" style={{ left: glide.left, width: glide.width }} />}
            <NavGroup
              items={navItems}
              active={view}
              onSelect={onAgentNav}
              labels={isWide}
              drag={isDesktop && !definable ? { dragKey, onDragStart: setDragKey, onDragEnter: dragOver, onDragEnd: dragEnd } : undefined}
            />
          </div>
        )}
      </nav>
    </header>
  );
}
